// orientation.ts — 画面の向き購読のみ。matchMedia 駆動 (SSR/jsdom 安全)。
// なぜ matchMedia か: App/Solo の既存 useMediaQuery と同形で、resize/回転に追従しつつ概念上の
// 「向き」だけを返す。OrientationGate が portrait のとき CSS で対局を 90°回転する判定に使う。
// 旧 lockLandscape/unlockOrientation (screen.orientation.lock) は廃止: fullscreen 副作用を伴い
// 「このサイトのためだけに OS の向きロックを解除させる」最悪 UX の温床だった。向きはロックしない。
import { useSyncExternalStore } from 'react';

type Orientation = 'portrait' | 'landscape';

const QUERY = '(orientation: portrait)';

// useSyncExternalStore: subscribe で matchMedia の change を購読、getSnapshot で現在値を返す。
// SSR/jsdom で matchMedia 不在・例外時は 'landscape' に倒す (gate が誤発火して対局を隠さない安全側)。
function subscribe(cb: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  let mql: MediaQueryList;
  try {
    mql = window.matchMedia(QUERY);
  } catch {
    return () => {};
  }
  // 古い Safari は addEventListener 非対応 → addListener にフォールバック。
  if (typeof mql.addEventListener === 'function') {
    mql.addEventListener('change', cb);
    return () => mql.removeEventListener('change', cb);
  }
  mql.addListener(cb);
  return () => mql.removeListener(cb);
}

function getSnapshot(): Orientation {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'landscape';
  try {
    return window.matchMedia(QUERY).matches ? 'portrait' : 'landscape';
  } catch {
    return 'landscape';
  }
}

// SSR snapshot は常に 'landscape' (gate を出さない = ハイドレーション不一致を避ける安全側)。
function getServerSnapshot(): Orientation {
  return 'landscape';
}

export function useOrientation(): Orientation {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
